import URLX, { relativeUrl } from '../Url';
import createSlidingKeyValueCache from '../sliding-key-value-cache';
import { getFastCache } from '../DynamicData';
import { isBrowser } from '../device-type';

export type CacheItem = Scope.IScopeModel & {
  url: string;
  componentName: string;
  currentTheme: string;
  meta: Scope.MetaInfo;
  $cache: 'full' | 'partial';
};

const cache = createSlidingKeyValueCache<CacheItem>(
  'currentpage',
  50,
  10,
  (item, url) => (item.url === url ? item : findIn(item, url)),
  item => item.shouldCache,
  item => item.shouldCache,
  item => item.shouldCache,
  item => item.url,
);

export type CacheUrlMatcher = (url: URLX) => string;

let cacheUrlMatcher: CacheUrlMatcher = url => relativeUrl(url, false);

/**
 * By default we will look for items in the cache where the `url` property exactly
 * matches the current url, and that works for most cases. But if you list products
 * on the PLP but have variation urls you can override the default behavior.
 *
 * Let's say that the product card data on the PLP looks like this:
 * {
 *   url: '/myproduct',
 *   code: 'P1',
 *   componentName: '...',
 *   variants: [
 *     {code: 'V1', url: '/myproduct?variant=V1'}
 *     {code: 'V2', url: '/myproduct?variant=V2'}
 *   ]
 * }
 *
 * And you want the link for the card to be one of the variant urls. In that
 * case we won't ever hit the partial cache by default, because we'll try to match
 * the root url (`/myproduct`) with the current url (`/myproduct?variant=V1`).
 *
 * In this case you can set your own matcher that does something like this:
 * setCacheUrlMatcher(url => {
 *   const parts = relativeUrl(url).split('?');
 *   return const[0];
 * });
 *
 * Which removes the query string and will look in the cache for `/myproduct` instead of
 * `/myproduct?variant=V1`
 */
export function setCacheUrlMatcher(matcher: CacheUrlMatcher) {
  cacheUrlMatcher = matcher;
}

export function isCacheItem(item: any): item is CacheItem {
  return !!(item && item.url && item.$cache && item.componentName && item.meta);
}

export async function getCacheItem(url: URLX): Promise<CacheItem | null> {
  const key = cacheUrlMatcher(url);
  let cacheItem = await cache.getCacheItem(key);
  if (cacheItem === null) {
    const dynamicDataFastCache = getFastCache();
    cacheItem = findIn(dynamicDataFastCache, key);
  }
  return cacheItem;
}

export function setCacheItem(item: CacheItem, fastCacheOnly = false): Promise<any> {
  return cache.setCacheItem(item, fastCacheOnly);
}

export function clearCacheItem(url: URLX) {
  const key = cacheUrlMatcher(url);
  return cache.clearCacheItem(key);
}

export function nukeCache() {
  return cache.nukeCache();
}
// This is used in _Splashscreen.html
if (isBrowser()) {
  window.nukeItemCache = nukeCache;
}

function findIn(cacheItem: object, url: string): CacheItem | null {
  let partialCacheOrNull: CacheItem | null = null;
  if (Array.isArray(cacheItem)) {
    for (const item of cacheItem) {
      if (isCacheItem(item) && url === item.url) {
        if (item.$cache === 'full') {
          return item;
        } else {
          partialCacheOrNull = item;
        }
      }

      // A partial never contains cache items
      if (!isCacheItem(item) || item.$cache !== 'partial') {
        const deep = findIn(item, url);
        if (deep) {
          return deep;
        }
      }
    }
  } else if (cacheItem instanceof Object) {
    if (isCacheItem(cacheItem) && url === cacheItem.url) {
      if (cacheItem.$cache === 'full') {
        return cacheItem;
      } else {
        partialCacheOrNull = cacheItem;
      }
    }

    // A partial never contains cache items
    if (!isCacheItem(cacheItem) || cacheItem.$cache !== 'partial') {
      const keys = Object.keys(cacheItem);
      for (const key of keys) {
        const item = (cacheItem as any)[key];
        if (isCacheItem(item) && url === item.url) {
          if (item.$cache === 'full') {
            return item;
          } else {
            partialCacheOrNull = item;
          }
        }
        const deep = findIn(item, url);
        if (deep) {
          return deep;
        }
      }
    }
  }
  return partialCacheOrNull;
}
